/**
 * WAF Edge Rules — HTTP header rewriting (Owl/Mlytics). Service-scoped
 * (path: `/services/:service_id/`) and domain-anchored (`?domain=`). A rule is an ordered list
 * (by priority) of match `conditions` + a single header-rewrite `action`
 * (`type: "replace"`, a list of request/response headers to set/inject).
 *
 * Backed by `modules/service/waf/controller_edge_rules.go`:
 *   - GET    /services/:service_id/waf/edge/rules            → a domain's rules (sorted by priority)
 *   - POST   /services/:service_id/waf/edge/rules            → create a rule
 *   - PATCH  /services/:service_id/waf/edge/rules/:id         → replace a rule (full body)
 *   - PATCH  /services/:service_id/waf/edge/rules/:id/enable  → toggle a rule on/off
 *   - DELETE /services/:service_id/waf/edge/rules/:id         → delete a rule
 *   - GET    /services/:service_id/waf/edge/replace-headers   → supported/unsupported header names
 *   - GET    /services/:service_id/waf/edge/dynamic-values    → insertable value tokens
 *
 * Edge rules have no schema endpoint — the condition vocabulary is identical to
 * firewall rules, so the builder reuses `useWafFirewallSchema`.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3, wafKeys, unwrap } from './_shared';

/**
 * One header to set/inject/remove (vendor `values[]`). `type` is the side
 * ("request" | "response"). `expression` is how `value` is interpreted:
 *   - "dynamic" → `value` is a dynamic token (e.g. "http.host")
 *   - "remove"  → the header is removed (`value` ignored)
 *   - omitted / "static" → `value` is a literal string
 */
export interface EdgeHeaderValue {
  type: string;
  name: string;
  value: string;
  expression?: string;
}

/**
 * The edge action. `type` selects the variant:
 *   - "redirect"   → `url`, `code`, `redirect_type`, `preserve_query_string`
 *   - "replace"    → `headers` (each header's `type` is "request" | "response").
 *                    The form's "Replace request/response header" pills lock every
 *                    header to one side; "Replace (legacy)" lets each header pick
 *                    its own side — all three serialise to `type: "replace"`.
 *   - "forceHttps" → no extra fields — backend WIP (form sends it ahead of support)
 */
export interface EdgeRuleAction {
  type: string;
  headers?: EdgeHeaderValue[];   // replace
  code?: number;                 // redirect
  url?: string;                  // redirect
  redirect_type?: string;        // redirect
  preserve_query_string?: boolean; // redirect
}

/** One match condition. `logic` ("and"|"or") connects to the NEXT condition. */
export interface EdgeRuleCondition {
  field: string;
  key?: string;
  operator: string;
  value?: string;
  values?: string[];
  logic?: string;
}

/** A stored edge rule (`GET /waf/edge/rules`, sorted by priority). */
export interface EdgeRule {
  id: string;
  rule_id: number;
  name: string;
  enable: boolean;
  priority: number;
  type: string;
  action: EdgeRuleAction;
  conditions: EdgeRuleCondition[];
  created_at?: number;
  updated_at?: number;
}

/** Create/update body (full replace on update). 1–5 conditions. */
export interface EdgeRuleInput {
  name: string;
  enable?: boolean;
  action: EdgeRuleAction;
  conditions: EdgeRuleCondition[];
  /** Records which catalog template the rule was cloned from (create only). */
  template_id?: string;
}

/** A read-only edge-rule template (clone source — action + conditions ready). */
export interface EdgeRuleTemplate {
  id: string;
  name: string;
  type: string;
  action: EdgeRuleAction;
  conditions: EdgeRuleCondition[];
  created_at?: number;
  updated_at?: number;
}

/** Header names the `replace` action may target. */
export interface EdgeReplaceHeaderNames {
  support: string[];
  unsupport: string[];
}

/** A domain's edge rules (sorted by priority). */
export function useWafEdgeRules(serviceId: string | undefined, domain: string | undefined) {
  return useQuery<EdgeRule[]>({
    queryKey: wafKeys.edgeRules(serviceId ?? '', domain ?? ''),
    enabled: !!serviceId && !!domain,
    staleTime: 15_000,
    queryFn: async () =>
      unwrap<EdgeRule[]>(
        await serviceApiV3.get(`/services/${serviceId}/waf/edge/rules`, { params: { domain } }),
      ) ?? [],
  });
}

/** Edge-rule template catalog (clone source for create). */
export function useWafEdgeTemplates(serviceId: string | undefined) {
  return useQuery<EdgeRuleTemplate[]>({
    queryKey: wafKeys.edgeTemplates(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 30 * 60_000,
    queryFn: async () =>
      unwrap<EdgeRuleTemplate[]>(
        await serviceApiV3.get(`/services/${serviceId}/waf/edge/templates`),
      ) ?? [],
  });
}

/** Supported / unsupported header names for the rewrite action. */
export function useWafEdgeReplaceHeaders(serviceId: string | undefined) {
  return useQuery<EdgeReplaceHeaderNames>({
    queryKey: wafKeys.edgeReplaceHeaders(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 30 * 60_000,
    queryFn: async () =>
      unwrap<EdgeReplaceHeaderNames>(
        await serviceApiV3.get(`/services/${serviceId}/waf/edge/replace-headers`),
      ) ?? { support: [], unsupport: [] },
  });
}

/** Dynamic value tokens insertable into a header value (e.g. `mly.real_client_ip`). */
export function useWafEdgeDynamicValues(serviceId: string | undefined) {
  return useQuery<string[]>({
    queryKey: wafKeys.edgeDynamicValues(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 30 * 60_000,
    queryFn: async () =>
      unwrap<string[]>(
        await serviceApiV3.get(`/services/${serviceId}/waf/edge/dynamic-values`),
      ) ?? [],
  });
}

function invalidateRules(qc: ReturnType<typeof useQueryClient>, serviceId?: string) {
  if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'edge-rules'] });
}

/** Create an edge rule (`POST /waf/edge/rules`). */
export function useCreateEdgeRule(
  serviceId: string | undefined,
  options?: UseMutationOptions<unknown, Error, { domain: string; input: EdgeRuleInput }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, input }: { domain: string; input: EdgeRuleInput }) =>
      unwrap(
        await serviceApiV3.post(`/services/${serviceId}/waf/edge/rules`, input, { params: { domain } }),
      ),
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}

/** Replace an edge rule (`PATCH /waf/edge/rules/:id`, full body). */
export function useUpdateEdgeRule(
  serviceId: string | undefined,
  options?: UseMutationOptions<unknown, Error, { domain: string; id: string; input: EdgeRuleInput }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, id, input }: { domain: string; id: string; input: EdgeRuleInput }) =>
      unwrap(
        await serviceApiV3.patch(`/services/${serviceId}/waf/edge/rules/${id}`, input, { params: { domain } }),
      ),
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}

/** Toggle an edge rule on/off (`PATCH /waf/edge/rules/:id/enable`). */
export function useSetEdgeRuleEnabled(
  serviceId: string | undefined,
  options?: UseMutationOptions<unknown, Error, { domain: string; id: string; enable: boolean }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, id, enable }: { domain: string; id: string; enable: boolean }) =>
      unwrap(
        await serviceApiV3.patch(`/services/${serviceId}/waf/edge/rules/${id}/enable`, { enable }, { params: { domain } }),
      ),
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}

/** Delete an edge rule (`DELETE /waf/edge/rules/:id`). */
export function useDeleteEdgeRule(
  serviceId: string | undefined,
  options?: UseMutationOptions<void, Error, { domain: string; id: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, id }: { domain: string; id: string }) => {
      await serviceApiV3.delete(`/services/${serviceId}/waf/edge/rules/${id}`, { params: { domain } });
    },
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}
